import { forwardRef, useImperativeHandle, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useQuery } from "react-query";

import { comboItemApi } from "src/1/apis/combo_item.api";
import { hangHoaApi } from "src/1/apis/hang_hoa.api";
import { CIconButton } from "src/1/common/components/controls";
import { CDrawer, CTable } from "src/1/common/components/others";
import { CButton, CInput, CSelect } from "src/common/components/controls";

import { comboItemResolver, defaultValues } from "../../form";

const FORM_MODE = {
  CREATE: "create",
  UPDATE: "update",
  VIEW: "view",
};

export const ComboItemForm = forwardRef(({ refetch }, ref) => {
  //#region Data
  const [formMode, setFormMode] = useState(FORM_MODE.CREATE);
  const [open, setOpen] = useState(false);

  const { control, handleSubmit, reset } = useForm({
    mode: "all",
    defaultValues: defaultValues,
    resolver: comboItemResolver,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "goods",
    keyName: "__id",
  });

  const { data: goods_options } = useQuery({
    queryKey: ["danh-sach-hang-hoa"],
    queryFn: () => hangHoaApi.getAll(),
    select: (response) =>
      response?.data?.data?.map((e) => ({
        code: e?.code,
        label: e?.name,
        unit: e?.unit,
        value: e?.code,
      })),
  });
  //#endregion

  //#region Event
  const onClose = () => {
    setOpen(false);
    setFormMode(FORM_MODE.CREATE);
    reset(defaultValues);
  };

  const onSubmit = handleSubmit(
    async (values) => {
      try {
        const payload = {
          ...values,
          goods: values?.goods?.map((e) => e?.code),
        };
        if (formMode === FORM_MODE.CREATE) {
          await comboItemApi.create(payload);
        } else {
          const { id } = values;
          await comboItemApi.update(id, payload);
        }
        refetch?.();
        noti("success", "Lưu combo item thành công!");
        onClose();
      } catch (error) {
        noti("error", error?.message ?? "Không thể lưu thông tin combo item!");
      }
    },
    (errors) => {
      const firstMessage = Object.values(errors)?.[0]?.message;
      noti("error", firstMessage);
    }
  );

  const onAddGood = () => append({ code: "", name: "", unit: "" });

  const onRemoveGood = (index) => () => remove(index);
  //#endregion

  useImperativeHandle(ref, () => ({
    open: async (id) => {
      if (id) {
        const response = await comboItemApi.getById(id);
        reset(response?.data?.data);
        setFormMode(FORM_MODE.UPDATE);
      }
      setOpen(true);
    },
    view: async (id) => {
      const response = await comboItemApi.getById(id);
      reset(response?.data?.data);
      setFormMode(FORM_MODE.VIEW);
      setOpen(true);
    },
  }));

  //#region Render
  const headers = [
    ...(formMode !== FORM_MODE.VIEW
      ? [
          {
            key: "action",
            label: "",
            style: { minWidth: 50 },
            cellRender: (value, record, index) => (
              <CIconButton
                color="#F26464"
                onClick={onRemoveGood(index)}
                icon={<i className="text-xl fa-solid fa-trash-can"></i>}
              />
            ),
          },
        ]
      : []),
    {
      key: "code",
      label: "mã hàng hóa",
      width: 300,
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`goods.${index}.code`}
          render={({ field }) => (
            <CSelect
              {...field}
              disabled={formMode === FORM_MODE.VIEW}
              className="w-full"
              options={goods_options}
              display="code"
              select="code"
            />
          )}
        />
      ),
    },
    {
      key: "name",
      label: "tên hàng hóa",
      width: 400,
      align: "left",
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`goods.${index}.code`}
          render={({ field }) => (
            <CSelect
              {...field}
              disabled={formMode === FORM_MODE.VIEW}
              className="w-full"
              options={goods_options}
              select="code"
            />
          )}
        />
      ),
    },
    {
      key: "unit",
      label: "đơn vị tính",
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`goods.${index}.code`}
          render={({ field }) => (
            <CSelect
              {...field}
              className="w-full"
              display="unit"
              options={goods_options}
              readOnly
            />
          )}
        />
      ),
    },
  ];

  return (
    <CDrawer direction="right" open={open} onClose={onClose}>
      <div className="p-3 flex flex-col gap-5 h-full overflow-hidden">
        <div className="grid grid-cols-2 gap-3">
          <Controller
            control={control}
            name="code"
            render={({ field }) => (
              <CInput {...field} label="Mã combo item" required readOnly />
            )}
          />
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <CInput
                {...field}
                disabled={formMode === FORM_MODE.VIEW}
                label="Tên combo item"
                placeholder="Nhập tên combo item"
                required
              />
            )}
          />
          <Controller
            control={control}
            name="unit"
            render={({ field }) => (
              <CInput
                {...field}
                disabled={formMode === FORM_MODE.VIEW}
                label="Đơn vị tính"
                placeholder="Nhập đơn vị tính"
                required
              />
            )}
          />
        </div>

        <div>
          <div className="flex flex-row gap-1 items-center mb-2">
            <h6 className="mb-0 font-semibold">
              Danh sách mặt hàng trong combo item
            </h6>
            {formMode !== FORM_MODE.VIEW && (
              <div className="flex items-center justify-center">
                <CIconButton
                  onClick={onAddGood}
                  icon={<i className="fa-lg fa-regular fa-circle-plus"></i>}
                />
              </div>
            )}
          </div>
          <div className="max-h-[500px] overflow-auto">
            <CTable
              rowKey="__id"
              headers={headers}
              data={fields}
              headerTransform="capitalize"
            />
          </div>
        </div>

        {formMode !== FORM_MODE.VIEW && (
          <div className="flex flex-row justify-center gap-3 items-center">
            <CButton onClick={onClose} color="danger">
              Hủy
            </CButton>
            <CButton onClick={onSubmit}>Lưu</CButton>
          </div>
        )}
      </div>
    </CDrawer>
  );
  //#endregion
});
